/* ============================================================================
   DRAGGABLE RAILS — one implementation, every rail that asks for it.

   Attaches to `[data-rz-drag]`, so a section opts in from its markup and there
   is no second copy of this in a section file. Two rails use it today (the
   product rail and the reviews) and a third would need no new code.

   ── MOUSE ONLY, DELIBERATELY ────────────────────────────────────────────────
   Touch already scrolls these natively, with momentum, rubber-banding and the
   platform's own snap behaviour. Taking `pointermove` over on touch means
   reimplementing all of that badly, so the handler returns immediately for any
   pointer that is not a mouse. What is being added is the ONE thing a mouse
   lacks: the ability to grab and pull.

   ── A DRAG MUST NOT FIRE THE LINK IT ENDED ON ───────────────────────────────
   Every card in the product rail is an anchor, so a drag that finishes over a
   card would otherwise navigate to it — the rail would be unusable with a mouse
   in exactly the way it is meant to be usable. The click is cancelled in the
   CAPTURE phase, before it reaches the anchor, and only when the pointer
   actually travelled past DRAG_SLOP. A 3px tremor still counts as a click.

   ── AND A RAIL WITH NOTHING FOCUSABLE IN IT GETS A TAB STOP ─────────────────
   Tabbing through the product rail scrolls it, because each card is a link and
   the browser brings a focused element into view. The reviews rail contains no
   links at all, so without this a keyboard user could reach review 1 and no
   further. Browsers scroll a focused scroll container with the arrow keys, so
   the fix is a tab stop and an accessible name — both only where they are
   needed, because an unnecessary tab stop is its own annoyance.
   ========================================================================== */
(() => {
  const DRAG_SLOP = 8; // px of travel before a click is treated as a drag

  const focusableIn = (el) =>
    el.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');

  const wire = (rail) => {
    if (rail.dataset.rzDragReady) return;
    rail.dataset.rzDragReady = '1';

    // Keyboard reach for a rail whose content is not itself focusable.
    if (!focusableIn(rail) && !rail.hasAttribute('tabindex')) {
      rail.setAttribute('tabindex', '0');
    }

    let down = false, startX = 0, startLeft = 0, moved = 0;

    rail.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = 0;
      startX = e.clientX;
      startLeft = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      if (moved > DRAG_SLOP) {
        rail.classList.add('is-dragging');
        // Once we are dragging, the browser's own text selection is in the way.
        e.preventDefault();
        rail.scrollLeft = startLeft - dx;
      }
    });

    const end = () => {
      down = false;
      rail.classList.remove('is-dragging');
      // `moved` is deliberately NOT reset here — the click that follows this
      // pointerup has not fired yet, and it is the thing that needs to know.
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);

    rail.addEventListener('click', (e) => {
      if (moved > DRAG_SLOP) { e.preventDefault(); e.stopPropagation(); }
      moved = 0;
    }, true); // capture: stop it before it reaches the card's anchor

    // A drag is not a selection.
    rail.addEventListener('dragstart', (e) => {
      if (rail.classList.contains('is-dragging')) e.preventDefault();
    });
  };

  const scan = (root) => (root.querySelectorAll?.('[data-rz-drag]') ?? []).forEach(wire);

  scan(document);
  // Shopify's theme editor re-inserts a whole section on every settings change,
  // so a one-shot scan leaves the rail dead for whoever is editing the page.
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
